import React, { useMemo, useState, useRef, useCallback } from 'react'
import { Stage as _Stage, Layer, RegularPolygon, Circle, Text, Group, Line, Image as _KonvaImage, Rect } from 'react-konva'
import useImage from 'use-image'
import { CatanHex, CatanPort, CatanVertex, CatanEdge, CatanPlayer, CatanKnight } from '../store/catan'
import { getVertexPixel, getEdgePixel, getAdjacentVertices, getAdjacentEdges, edgeAdjacentVertices as edgeAdjacentVerticesLocal } from '../util/catanCoord'

// react-konva 18.x 타입 workaround: React 18에서 FC가 children을 자동 포함하지 않음
// Stage·KonvaImage는 실제로 children을 받으므로 any 캐스팅으로 우회
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Stage = _Stage as any
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const KonvaImage = _KonvaImage as any

// ── 플레이어 색상 (서버 COLOR 문자열 → CSS hex) ────────────
const PLAYER_COLOR_HEX: Record<string, string> = {
  RED:    '#D32F2F',
  BLUE:   '#1976D2',
  GREEN:  '#388E3C',
  ORANGE: '#F57C00',
  BROWN:  '#6D4C41',
  WHITE:  '#FAFAFA',
}

// ── 자원 색상 매핑 (rope·자원 권한 마커·fallback에만 사용) ──
// 2026-05-01: hex 본체 fill은 hex-tile 이미지로 교체. 색상은 항구 rope/마커에만 잔존.
const RESOURCE_COLOR: Record<string, string> = {
  WOOL:   '#9DD17B',
  WHEAT:  '#F5D547',
  BRICK:  '#C75450',
  ORE:    '#7A7E89',
  WOOD:   '#3F8A4D',
  DESERT: '#E8D29A',
}

// ── hex 타일 자산 매핑 (자원 → 1024x1024 지형 PNG) ─────────
// 2026-05-01: 색상 fill 폐기 → SDXL 카툰 hex-tile 자산 6종 통합
const HEX_TILE_ASSET: Record<string, string> = {
  WOOD:   '/assets/catan/hex-tile/forest.png',
  ORE:    '/assets/catan/hex-tile/mountain.png',
  BRICK:  '/assets/catan/hex-tile/hills.png',
  WOOL:   '/assets/catan/hex-tile/pasture.png',
  WHEAT:  '/assets/catan/hex-tile/fields.png',
  DESERT: '/assets/catan/hex-tile/desert.png',
}

// ── 자원 아이콘 자산 매핑 (자원 → 512x512 vector PNG) ──────
// 2026-05-01: 이모지 → SDXL vector 자원 아이콘 5종 통합 (hex 위 표기 + 자원 패널 공유)
const RESOURCE_ICON_ASSET: Record<string, string> = {
  WOOD:  '/assets/catan/resource/wood.png',
  ORE:   '/assets/catan/resource/ore.png',
  BRICK: '/assets/catan/resource/brick.png',
  WOOL:  '/assets/catan/resource/sheep.png',
  WHEAT: '/assets/catan/resource/wheat.png',
}

// ── 항구 자산 매핑 (resourceType → 정적 자산 경로) ──────────
// ADR-001 §2-1, Phase 7 통합 사양 §2-2
// 2026-05-06: 자원 이미지 재사용으로 통합 (board-piece 분리 자산 → resource 디렉토리 공유).
//   · 5종 자원 항구(2:1): RESOURCE 자원 이미지 재사용 (헥스/카드와 톤 일치)
//   · 일반 3:1 항구 4곳: question.png (모든 자원 가능 의미)
const PORT_ASSET: Record<string, string> = {
  GENERIC: '/assets/catan/resource/question.png',  // ratio=3 일반 항구
  WOOD:    '/assets/catan/resource/wood.png',
  BRICK:   '/assets/catan/resource/brick.png',
  WOOL:    '/assets/catan/resource/sheep.png',
  WHEAT:   '/assets/catan/resource/wheat.png',
  ORE:     '/assets/catan/resource/ore.png',
}
// ── axial → pixel 변환 (pointy-top) ───────────────────────
function axialToPixel(q: number, r: number, size: number) {
  return {
    x: size * (Math.sqrt(3) * q + (Math.sqrt(3) / 2) * r),
    y: size * (1.5 * r),
  }
}

// ── vertex 픽셀 좌표 계산 ─────────────────────────────────
// pointy-top 헥스의 6개 꼭짓점 (코너 0~5, 상단=0, 시계방향)
function hexCornerPixel(cx: number, cy: number, size: number, corner: number) {
  const angle = (Math.PI / 180) * (60 * corner - 30) // pointy-top: 상단 꼭짓점이 -30°
  return {
    x: cx + size * Math.cos(angle),
    y: cy + size * Math.sin(angle),
  }
}

// 숫자 확률 점 개수 (2~12, 7 제외)
function dotCount(n: number): number {
  const map: Record<number, number> = {
    2: 1, 12: 1,
    3: 2, 11: 2,
    4: 3, 10: 3,
    5: 4, 9: 4,
    6: 5, 8: 5,
  }
  return map[n] ?? 0
}

// 항구 자원 짧은 이름 (한글)
function portResourceShort(r: string | null): string {
  const map: Record<string, string> = {
    WOOL: '양', WHEAT: '곡식', BRICK: '벽돌', ORE: '광물', WOOD: '나무',
  }
  return r ? (map[r] ?? r) : ''
}

// 항구 무역 툴팁 한글 문장
function portTooltip(p: CatanPort): string {
  if (p.ratio === 3) return '아무 자원 3장 → 다른 자원 1장'
  return `${portResourceShort(p.resourceType)} 2장 → 다른 자원 1장`
}

/** Phase 4-3 본 턴 건설 모드 (MAIN_TURN_POSTROLL에서만 활성). null=비빌드 모드 */
export type BuildMode = 'BUILD_ROAD' | 'BUILD_SETTLEMENT' | 'BUILD_CITY' | null

interface Props {
  hexes:          CatanHex[]
  ports:          CatanPort[]
  vertices:       CatanVertex[]
  edges:          CatanEdge[]
  players:        CatanPlayer[]
  // SEQ=10113 P8-CK-B — EXT_CK 기사 토큰 렌더용 (옵션, OFF 룸은 빈 배열 또는 미전달)
  knights?:       CatanKnight[]
  // SEQ=10114 P8-CK-C — EXT_CK 도시 성벽 vertex 집합 (옵션, 도시 위 🧱 마커 표시)
  wallVertexIds?: number[]
  stageW:         number
  stageH:         number
  hexSize:        number
  // 본인 정보
  myUserCode:     number
  myColor:        string
  // 게임 상태
  currentPhase:   string | null
  isMyTurn:       boolean
  // 방금 놓은 마을 vertex_id (PLACE_ROAD 페이즈에서 인접 edge 강조용)
  lastSettlementVertexId: number | null
  /** Phase 4-3 본 턴 건설 모드 (MAIN_TURN_POSTROLL 전용) */
  buildMode:      BuildMode
  /** Phase 6 발전카드 인라인 모드 (2026-05-07 fix)
   *   'KNIGHT_PICK_HEX' = 도적 이동 헥스 선택 (PREROLL/POSTROLL 모두)
   *   'ROAD_BUILDING_PICK_EDGE' = 도로 2개 무료 건설 edge 선택 */
  devCardMode?:   'KNIGHT_PICK_HEX' | 'ROAD_BUILDING_PICK_EDGE' | null
  /** ROAD_BUILDING_PICK_EDGE 모드에서 이미 선택 완료된 edge id 목록 (2개 중 1개 선택 후 전달)
   *  이 edge들은 클릭 후보(가이드·클릭 영역)에서 제외되어 동일 edge 재선택 UI를 방지 */
  roadBuildSelectedEdges?: number[]
  // 클릭 핸들러
  onVertexClick:  (vertexId: number) => void
  onEdgeClick:    (edgeId: number) => void
  /** 도적 이동(MAIN_TURN_MOVE_ROBBER)에서만 활성화 */
  onHexClick?:    (hexId: number) => void
  /** Phase 5 항구 클릭 — 본인 권한 항구 + POSTROLL 본인 턴에서 PortTrade 모달 진입 (D-8) */
  onPortClick?:   (portId: number) => void
  // ─────────────────────────────────────────────────────────
  // SEQ=10125 P8-CK-H2 (a) — 기사 클릭 단일 진입점
  //   진보카드 9 UX 중 POL_DESERTER / POL_INTRIGUE 의 1단계(상대 기사 pick)에서만 활성화.
  //   클릭 시 (knightId, ownerUserCode) 동시 전달 → Play.tsx 가 카드별 분기.
  //   클릭 활성 조건은 Play.tsx 가 knightClickMode props 로 제어.
  //   knightClickMode = null  → 본 핸들러 비활성(listening=false, 기존 동작 보존)
  //   knightClickMode != null → 본 핸들러 활성(listening=true, hover 강조)
  // ─────────────────────────────────────────────────────────
  /** 기사 클릭 모드 (null=비활성, 'DESERTER'/'INTRIGUE'=활성 + UX 톤 분기) */
  knightClickMode?: null | 'DESERTER' | 'INTRIGUE'
  /** 기사 클릭 콜백 (knightClickMode 활성 시만 호출) */
  onKnightClick?:  (knightId: number, ownerUserCode: number) => void
}

export default function CatanBoard({
  hexes, ports, vertices, edges, players,
  knights,
  wallVertexIds,
  stageW, stageH, hexSize,
  myUserCode, myColor,
  currentPhase, isMyTurn, lastSettlementVertexId,
  buildMode, devCardMode, roadBuildSelectedEdges,
  onVertexClick, onEdgeClick, onHexClick, onPortClick,
  knightClickMode, onKnightClick,
}: Props) {
  const centerX = stageW / 2
  const centerY = stageH / 2

  // ── 정적 자산 사전 로드 (2026-05-01 user 톤 v2 일괄 교체) ────
  // 마을/도시/도적·항구 6종 = 사용자 톤. hex 6종 + 자원 5종은 직전 통합 유지.
  // 도로는 플레이어 색을 동적으로 입혀야 하므로 Konva.Line 유지(road.png는 참조용).
  // 2026-05-07: 사용자 자산 town.jpg / castle.jpg 적용 (옛 settlement.png / city.png는 _user_jpg_archive 백업)
  const [settlementImg] = useImage('/assets/catan/board-piece/town.jpg')
  const [cityImg]       = useImage('/assets/catan/board-piece/castle.jpg')
  const [robberImg]     = useImage('/assets/catan/board-piece/robber.png')
  // 항구 자산 6종 (board-piece/)
  const [portGenericImg] = useImage(PORT_ASSET.GENERIC)
  const [portWoodImg]    = useImage(PORT_ASSET.WOOD)
  const [portBrickImg]   = useImage(PORT_ASSET.BRICK)
  const [portWoolImg]    = useImage(PORT_ASSET.WOOL)
  const [portWheatImg]   = useImage(PORT_ASSET.WHEAT)
  const [portOreImg]     = useImage(PORT_ASSET.ORE)
  function portImg(p: CatanPort): HTMLImageElement | undefined {
    if (p.ratio === 3) return portGenericImg
    switch (p.resourceType) {
      case 'WOOD':  return portWoodImg
      case 'BRICK': return portBrickImg
      case 'WOOL':  return portWoolImg
      case 'WHEAT': return portWheatImg
      case 'ORE':   return portOreImg
      default:      return portGenericImg
    }
  }
  // hex 타일 자산 6종 (hex-tile/, 1024x1024)
  const [hexForestImg]   = useImage(HEX_TILE_ASSET.WOOD)
  const [hexMountainImg] = useImage(HEX_TILE_ASSET.ORE)
  const [hexHillsImg]    = useImage(HEX_TILE_ASSET.BRICK)
  const [hexPastureImg]  = useImage(HEX_TILE_ASSET.WOOL)
  const [hexFieldsImg]   = useImage(HEX_TILE_ASSET.WHEAT)
  const [hexDesertImg]   = useImage(HEX_TILE_ASSET.DESERT)
  function hexImg(rt: string): HTMLImageElement | undefined {
    switch (rt) {
      case 'WOOD':   return hexForestImg
      case 'ORE':    return hexMountainImg
      case 'BRICK':  return hexHillsImg
      case 'WOOL':   return hexPastureImg
      case 'WHEAT':  return hexFieldsImg
      case 'DESERT': return hexDesertImg
      default:       return undefined
    }
  }
  // 자원 아이콘 자산 5종 (resource/, 512x512) — hex 위 자원 표기에 사용
  const [resWoodImg]  = useImage(RESOURCE_ICON_ASSET.WOOD)
  const [resOreImg]   = useImage(RESOURCE_ICON_ASSET.ORE)
  const [resBrickImg] = useImage(RESOURCE_ICON_ASSET.BRICK)
  const [resWoolImg]  = useImage(RESOURCE_ICON_ASSET.WOOL)
  const [resWheatImg] = useImage(RESOURCE_ICON_ASSET.WHEAT)
  function resourceImg(rt: string): HTMLImageElement | undefined {
    switch (rt) {
      case 'WOOD':  return resWoodImg
      case 'ORE':   return resOreImg
      case 'BRICK': return resBrickImg
      case 'WOOL':  return resWoolImg
      case 'WHEAT': return resWheatImg
      default:      return undefined
    }
  }

  // ── 헥스 픽셀 좌표 사전 계산 ──────────────────────────────
  const hexPixels = useMemo(() => {
    return hexes.map(h => {
      const { x, y } = axialToPixel(h.q, h.r, hexSize)
      return { ...h, px: centerX + x, py: centerY + y }
    })
  }, [hexes, hexSize, centerX, centerY])

  // ── 플레이어 userCode → color 매핑 ──────────────────────
  const playerColorMap = useMemo(() => {
    const m: Record<number, string> = {}
    for (const p of players) {
      m[p.userCode] = PLAYER_COLOR_HEX[p.color] ?? '#ccc'
    }
    return m
  }, [players])

  // ── vertex 픽셀 좌표 (54개 사전 계산) ───────────────────
  const vertexPixels = useMemo(() => {
    const map: Record<number, { x: number; y: number }> = {}
    for (let v = 1; v <= 54; v++) {
      map[v] = getVertexPixel(v, hexSize, centerX, centerY)
    }
    return map
  }, [hexSize, centerX, centerY])

  // ── edge 픽셀 좌표 (72개 사전 계산) ────────────────────
  const edgePixels = useMemo(() => {
    const map: Record<number, { x1: number; y1: number; x2: number; y2: number }> = {}
    for (let e = 1; e <= 72; e++) {
      map[e] = getEdgePixel(e, hexSize, centerX, centerY)
    }
    return map
  }, [hexSize, centerX, centerY])

  // ── 클릭 가능 판별 ──────────────────────────────────────
  // PLACE_SETTLEMENT: 빈 vertex + 인접 vertex에 마을 없음
  const occupiedVertexIds = useMemo(
    () => new Set(vertices.filter(v => v.ownerUserCode != null).map(v => v.vertexId)),
    [vertices]
  )
  const occupiedEdgeIds = useMemo(
    () => new Set(edges.filter(e => e.ownerUserCode != null).map(e => e.edgeId)),
    [edges]
  )

  // PLACE_ROAD: 방금 놓은 마을 인접 edge 집합
  const settlementAdjacentEdgeIds = useMemo(() => {
    if (!lastSettlementVertexId) return new Set<number>()
    return new Set(getAdjacentEdges(lastSettlementVertexId))
  }, [lastSettlementVertexId])

  // ── Phase 4-3 본인 점유 집합 (BUILD_ROAD/SETTLEMENT/CITY 가이드 산출용) ──
  // 본인 도로 edge 집합 (BUILD_ROAD/SETTLEMENT 인접 검증용)
  const myEdgeIds = useMemo(
    () => new Set(edges.filter(e => e.ownerUserCode === myUserCode).map(e => e.edgeId)),
    [edges, myUserCode]
  )
  // 본인 마을/도시 vertex 집합 (BUILD_ROAD 인접 + BUILD_CITY 후보)
  const myVertexIds = useMemo(
    () => new Set(vertices.filter(v => v.ownerUserCode === myUserCode).map(v => v.vertexId)),
    [vertices, myUserCode]
  )
  // 본인 마을(도시 아님) vertex 집합 (BUILD_CITY 후보)
  const mySettlementVertexIds = useMemo(
    () => new Set(
      vertices
        .filter(v => v.ownerUserCode === myUserCode && v.buildingType === 'SETTLEMENT')
        .map(v => v.vertexId)
    ),
    [vertices, myUserCode]
  )
  // BUILD_ROAD 가이드용 — 본인 도로/마을/도시에 인접한 미점유 edge 집합
  const buildRoadCandidateEdgeIds = useMemo(() => {
    if (buildMode !== 'BUILD_ROAD') return new Set<number>()
    const candidates = new Set<number>()
    // (a) 본인 마을/도시 vertex의 인접 edge
    myVertexIds.forEach(vId => {
      getAdjacentEdges(vId).forEach(eId => {
        if (!occupiedEdgeIds.has(eId)) candidates.add(eId)
      })
    })
    // (b) 본인 도로의 양 끝 vertex의 인접 edge
    myEdgeIds.forEach(eId => {
      const verts = edgeAdjacentVerticesLocal[eId] ?? []
      verts.forEach(vId => {
        getAdjacentEdges(vId).forEach(adjEId => {
          if (!occupiedEdgeIds.has(adjEId)) candidates.add(adjEId)
        })
      })
    })
    return candidates
  }, [buildMode, myVertexIds, myEdgeIds, occupiedEdgeIds])

  // BUILD_SETTLEMENT 가이드용 — 본인 도로 인접 + 미점유 + 2칸 거리 룰 통과 vertex
  const buildSettlementCandidateVertexIds = useMemo(() => {
    if (buildMode !== 'BUILD_SETTLEMENT') return new Set<number>()
    const result = new Set<number>()
    for (let v = 1; v <= 54; v++) {
      if (occupiedVertexIds.has(v)) continue
      // 인접 edge 중 본인 도로 ≥1
      const adjE = getAdjacentEdges(v)
      const hasMyRoad = adjE.some(eId => myEdgeIds.has(eId))
      if (!hasMyRoad) continue
      // 2칸 거리 룰
      const adjV = getAdjacentVertices(v)
      const tooClose = adjV.some(av => occupiedVertexIds.has(av))
      if (tooClose) continue
      result.add(v)
    }
    return result
  }, [buildMode, myEdgeIds, occupiedVertexIds])

  // BUILD_CITY 가이드용 — 본인 마을 vertex
  const buildCityCandidateVertexIds = useMemo(() => {
    if (buildMode !== 'BUILD_CITY') return new Set<number>()
    return new Set(mySettlementVertexIds)
  }, [buildMode, mySettlementVertexIds])

  function isVertexClickable(vertexId: number): boolean {
    if (!isMyTurn) return false
    if (currentPhase === 'PLACE_SETTLEMENT') {
      if (occupiedVertexIds.has(vertexId)) return false
      return getAdjacentVertices(vertexId).every(av => !occupiedVertexIds.has(av))
    }
    if (currentPhase === 'MAIN_TURN_POSTROLL') {
      if (buildMode === 'BUILD_SETTLEMENT') return buildSettlementCandidateVertexIds.has(vertexId)
      if (buildMode === 'BUILD_CITY')       return buildCityCandidateVertexIds.has(vertexId)
    }
    return false
  }

  function isEdgeClickable(edgeId: number): boolean {
    if (!isMyTurn) return false
    if (currentPhase === 'PLACE_ROAD') {
      if (occupiedEdgeIds.has(edgeId)) return false
      return settlementAdjacentEdgeIds.has(edgeId)
    }
    if (currentPhase === 'MAIN_TURN_POSTROLL' && buildMode === 'BUILD_ROAD') {
      return buildRoadCandidateEdgeIds.has(edgeId)
    }
    /* 2026-05-07 fix: ROAD_BUILDING 카드 인라인 모드 — 본인 도로/마을 인접 미점유 edge 활성화 (자원 차감 없음) */
    /* 2026-05-09 fix: roadBuildSelectedEdges(1번째로 선택한 edge)를 candidates에서 제외
     *   — 선택 완료된 edge를 보드에서 다시 클릭 가능하게 보이는 UX 버그 수정.
     *     occupiedEdgeIds는 서버 confirmed 상태만 반영하므로, 클라 임시 선택을
     *     별도 Set으로 차단. handleEdgeClick의 roadBuildEdges.includes() 가드는 보존. */
    if (devCardMode === 'ROAD_BUILDING_PICK_EDGE') {
      // 자원 검증 없이 BUILD_ROAD 후보 집합 재사용
      // (BUILD_ROAD 후보는 buildMode 의존 useMemo라 ROAD_BUILDING 모드에서는 다시 계산)
      const alreadySelected = new Set<number>(roadBuildSelectedEdges ?? [])
      const candidates = new Set<number>()
      myVertexIds.forEach(vId => {
        getAdjacentEdges(vId).forEach(eId => {
          if (!occupiedEdgeIds.has(eId) && !alreadySelected.has(eId)) candidates.add(eId)
        })
      })
      myEdgeIds.forEach(eId => {
        const verts = edgeAdjacentVerticesLocal[eId] ?? []
        verts.forEach(vId => {
          getAdjacentEdges(vId).forEach(adjEId => {
            if (!occupiedEdgeIds.has(adjEId) && !alreadySelected.has(adjEId)) candidates.add(adjEId)
          })
        })
      })
      // chain: 1번째로 선택한 edge와 vertex를 공유하는 edge도 후보로 추가 (본인 자산 없는 순수 chain 허용)
      alreadySelected.forEach(selEId => {
        const selVerts = edgeAdjacentVerticesLocal[selEId] ?? []
        selVerts.forEach(vId => {
          getAdjacentEdges(vId).forEach(chainEId => {
            if (!occupiedEdgeIds.has(chainEId) && !alreadySelected.has(chainEId)) candidates.add(chainEId)
          })
        })
      })
      return candidates.has(edgeId)
    }
    return false
  }

  // hover 상태
  const [hoverVertexId, setHoverVertexId] = useState<number | null>(null)
  const [hoverEdgeId,   setHoverEdgeId]   = useState<number | null>(null)
  // 항구 hover/탭 (모바일 토글)
  const [activePortId,  setActivePortId]  = useState<number | null>(null)

  const isMobile = window.innerWidth < 768
  const vertexHitRadius = isMobile ? 18 : 12
  const edgeHitWidth    = isMobile ? 14 : 12
  // 모바일 터치 정확도 개선 (2026-05-01): 시각은 그대로 유지하되
  // hit area를 손가락 평균(~44px)에 맞춰 확장. Konva hitStrokeWidth로
  // 시각 stroke와 분리해 다른 요소(마을·도시) 영역과 충돌 회피.
  const edgeHitStrokeWidth   = isMobile ? 32 : 18
  const vertexHitStrokeWidth = isMobile ? 12 : 0  // Circle radius 위에 추가

  // ══════════════════════════════════════════════════════════
  // SEQ=10080 — 모바일 터치 UX (대책 A: 핀치/더블탭 줌 + 패닝 / 대책 E: 최근접 스냅)
  //   데스크톱(!isMobile)은 일절 영향 없음 — 모든 제스처 핸들러가 isMobile 게이트.
  //   Konva Stage 의 scale/position 을 직접 조작 → 자식 shape 의 onTap 좌표는
  //   Konva 가 자동으로 stage 로컬좌표로 변환하므로 기존 onVertexClick 등 그대로 동작.
  // ══════════════════════════════════════════════════════════
  const MIN_SCALE = 1
  const MAX_SCALE = 3.2
  const DOUBLE_TAP_SCALE = 2.2          // 더블탭 줌인 배율
  const TAP_MOVE_THRESHOLD = 10         // 이 픽셀 이상 이동 시 = 팬(탭 무효)
  const DOUBLE_TAP_MS = 320             // 더블탭 인정 간격
  const SNAP_MAX_DIST = 26              // 대책 E: 최근접 스냅 허용 거리(stage 로컬좌표 px)

  const stageRef = useRef<any>(null)
  // 줌/팬 상태 — 모바일에서만 비-기본값이 됨
  const [zoomScale, setZoomScale] = useState(1)
  const [stagePos, setStagePos]   = useState({ x: 0, y: 0 })

  // 제스처 추적용 ref (리렌더 유발 안 하도록 ref 사용)
  const gestureRef = useRef({
    // 1포인터 (팬/탭 판별)
    isPointerDown: false,
    pointerStart:  { x: 0, y: 0 },   // 화면좌표
    panOrigin:     { x: 0, y: 0 },   // 팬 시작 시 stagePos
    moved:         false,            // TAP_MOVE_THRESHOLD 초과 여부
    // 2포인터 (핀치)
    pinchActive:   false,
    pinchStartDist: 0,
    pinchStartScale: 1,
    pinchCenter:   { x: 0, y: 0 },   // 화면좌표 (핀치 중점)
    pinchOrigin:   { x: 0, y: 0 },   // 핀치 시작 시 stagePos
    // 더블탭
    lastTapTime:   0,
    lastTapPos:    { x: 0, y: 0 },
  })

  // 두 터치 포인트 거리/중점
  function touchDist(t0: Touch, t1: Touch) {
    return Math.hypot(t0.clientX - t1.clientX, t0.clientY - t1.clientY)
  }
  function touchCenter(t0: Touch, t1: Touch) {
    return { x: (t0.clientX + t1.clientX) / 2, y: (t0.clientY + t1.clientY) / 2 }
  }

  // scale 을 clamp + 특정 화면좌표(focal)를 고정점으로 줌
  const applyZoom = useCallback((newScaleRaw: number, focalScreen: { x: number; y: number }) => {
    const stage = stageRef.current
    if (!stage) return
    const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, newScaleRaw))
    const box = stage.container().getBoundingClientRect()
    // focal 의 stage 컨테이너 내 좌표
    const fx = focalScreen.x - box.left
    const fy = focalScreen.y - box.top
    // 현재 변환에서 focal 의 보드 로컬좌표
    const localX = (fx - stagePos.x) / zoomScale
    const localY = (fy - stagePos.y) / zoomScale
    // 새 scale 에서 focal 이 같은 화면 위치를 유지하도록 position 재계산
    let nx = fx - localX * newScale
    let ny = fy - localY * newScale
    // scale=1 로 돌아오면 position 도 0 (전체 보기 원위치)
    if (newScale === 1) { nx = 0; ny = 0 }
    setZoomScale(newScale)
    setStagePos({ x: nx, y: ny })
  }, [zoomScale, stagePos])

  // 패닝 — 화면좌표 델타만큼 position 이동 (scale 범위 밖으로 과도 이탈 시 clamp)
  const applyPan = useCallback((origin: { x: number; y: number }, dx: number, dy: number) => {
    if (zoomScale <= 1) return  // 줌 안 된 상태는 패닝 불필요
    // 보드가 화면 밖으로 너무 나가지 않게 — stage 크기 기준 ±(scale-1)*size 범위로 clamp
    const maxOffX = (zoomScale - 1) * stageW
    const maxOffY = (zoomScale - 1) * stageH
    const nx = Math.max(-maxOffX, Math.min(0, origin.x + dx))
    const ny = Math.max(-maxOffY, Math.min(0, origin.y + dy))
    setStagePos({ x: nx, y: ny })
  }, [zoomScale, stageW, stageH])

  // 전체 보기 리셋
  const resetView = useCallback(() => {
    setZoomScale(1)
    setStagePos({ x: 0, y: 0 })
  }, [])

  // 점 → 선분 최단거리 (대책 E edge 스냅용 — SEQ=10081)
  //   edge 는 선분이라 "중점 거리"로 근사하면 끝부분 탭이 엉뚱한 edge 로 스냅됨.
  //   점-선분 거리로 정확화: 탭이 edge 선분 위 어디든 가까우면 그 edge 로 스냅.
  function pointToSegmentDist(px: number, py: number, x1: number, y1: number, x2: number, y2: number): number {
    const dx = x2 - x1, dy = y2 - y1
    const lenSq = dx * dx + dy * dy
    if (lenSq === 0) return Math.hypot(px - x1, py - y1)
    let t = ((px - x1) * dx + (py - y1) * dy) / lenSq
    t = Math.max(0, Math.min(1, t))
    return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy))
  }

  // ── 대책 E: 최근접 스냅 — 탭 좌표에서 가장 가까운 clickable 타겟 선택 ──
  //   Stage onTap 에서 직접 shape 가 안 잡혔을 때만 보조로 동작 (shape onTap 이 먼저 처리됨).
  //   stage 로컬좌표(보드좌표) 기준 거리 계산.
  //   SEQ=10081: edge 는 점-선분 거리로 정확화 (기존 중점 근사 → 끝부분 탭 오스냅 수정).
  const snapToNearest = useCallback((localX: number, localY: number) => {
    let bestKind: 'vertex' | 'edge' | null = null
    let bestId = -1
    let bestDist = SNAP_MAX_DIST
    // clickable vertex 후보 — 점 거리
    for (let v = 1; v <= 54; v++) {
      if (!isVertexClickable(v)) continue
      const p = vertexPixels[v]
      if (!p) continue
      const d = Math.hypot(p.x - localX, p.y - localY)
      if (d < bestDist) { bestDist = d; bestKind = 'vertex'; bestId = v }
    }
    // clickable edge 후보 — 점-선분 거리 (edge 선분 위 어느 지점이든 가까우면 스냅)
    for (let e = 1; e <= 72; e++) {
      if (!isEdgeClickable(e)) continue
      const p = edgePixels[e]
      if (!p) continue
      const d = pointToSegmentDist(localX, localY, p.x1, p.y1, p.x2, p.y2)
      if (d < bestDist) { bestDist = d; bestKind = 'edge'; bestId = e }
    }
    if (bestKind === 'vertex') onVertexClick(bestId)
    else if (bestKind === 'edge') onEdgeClick(bestId)
  }, [vertexPixels, edgePixels, onVertexClick, onEdgeClick,
      isVertexClickable, isEdgeClickable])

  // ── 항구 시각: vertex midpoint + 외향 normal × hexSize × 0.7 ──
  // ADR-001 §2-2, Phase 7 통합 사양 §2-1
  // 외곽 9등분(폐기) → 실 vertex_id_1/_2 페어 기반 위치 산출.
  const portItems = useMemo(() => {
    return ports.map(p => {
      const v1 = vertexPixels[p.vertexId1]
      const v2 = vertexPixels[p.vertexId2]
      if (!v1 || !v2) return null
      // midpoint
      const mx = (v1.x + v2.x) / 2
      const my = (v1.y + v2.y) / 2
      // 보드 중심 → midpoint 정규 벡터 (외향 normal)
      const dx = mx - centerX
      const dy = my - centerY
      const len = Math.hypot(dx, dy) || 1
      const nx = dx / len
      const ny = dy / len
      // 항구 자산 중심 = midpoint + n × hexSize × 0.8 (헥스 변 직외측, 자연 인접)
      // 2026-05-06 v5: 사용자 시각 — v4 1.5는 너무 멈 + 잘림. v5 0.8 적정값 채택.
      //   midpoint(외곽 vertex 페어 평균) ≈ 2.5 hexSize, 헥스 변 직외측에 자연 인접.
      //   port 안쪽 끝 ≈ 2.75 (헥스 변 살짝 외측, 가림 미미). 외측 끝 ≈ 3.85 < Stage 반경 5.5.
      const offset = hexSize * 0.8
      const px = mx + nx * offset
      const py = my + ny * offset
      // 자산 회전 = 외향 방향 + 90° (자산 위쪽이 보드 외측을 향하도록)
      // top-down 3/4 view 자산은 회전 없이도 충분히 자연스러우므로 회전 0 유지(통합 사양 §7 잔여 권고)
      return {
        port: p,
        v1, v2,
        midX: mx, midY: my,
        px, py,
      }
    }).filter((x): x is NonNullable<typeof x> => x !== null)
  }, [ports, vertexPixels, centerX, centerY, hexSize])

  // 본인 권한 보유 항구 집합 (vertex_id_1/2에 본인 마을/도시 보유)
  const myPortPrivilege = useMemo(() => {
    const ownedVertices = new Set(
      vertices.filter(v => v.ownerUserCode === myUserCode).map(v => v.vertexId)
    )
    const set = new Set<number>()
    for (const p of ports) {
      if (ownedVertices.has(p.vertexId1) || ownedVertices.has(p.vertexId2)) {
        set.add(p.portId)
      }
    }
    return set
  }, [vertices, ports, myUserCode])

  // ── 항구 자산 합성 크기 ────────────────────────────────
  // 2026-05-06 v5: 1.2 → 1.1 추가 미세 축소 (잘림 방지 + 헥스 변 직외측 자연 인접).
  //   절반 = 0.55 hexSize.
  const portAssetSize = hexSize * 1.1

  return (
    <div style={{ position: 'relative', display: 'inline-block', lineHeight: 0 }}>
    <Stage
      ref={stageRef}
      width={stageW}
      height={stageH}
      // SEQ=10080 — 모바일 줌/팬: scale·position 으로 변환. 데스크톱은 항상 1 / {0,0}.
      scaleX={zoomScale}
      scaleY={zoomScale}
      x={stagePos.x}
      y={stagePos.y}
      style={{
        display: 'block',
        // 모바일 페이지 스크롤·핀치줌과 보드 탭 충돌 방지 (Phase 4-3 모바일 UX 개선)
        touchAction: 'none',
        WebkitUserSelect: 'none',
        userSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
      }}
      onClick={(e: any) => {
        // Stage 빈 곳 클릭 시 항구 활성화 해제 (모바일 외부 탭 토글)
        if (e.target === e.target.getStage()) {
          setActivePortId(null)
        }
      }}
      onTap={(e: any) => {
        if (e.target === e.target.getStage()) {
          setActivePortId(null)
        }
      }}
      // ── SEQ=10080 모바일 터치 제스처 (isMobile 게이트 — 데스크톱 무영향) ──
      onTouchStart={isMobile ? (e: any) => {
        const touches: TouchList = e.evt.touches
        const g = gestureRef.current
        if (touches.length === 2) {
          // 핀치 시작
          e.evt.preventDefault?.()
          g.pinchActive    = true
          g.isPointerDown  = false
          g.pinchStartDist = touchDist(touches[0], touches[1])
          g.pinchStartScale = zoomScale
          g.pinchCenter    = touchCenter(touches[0], touches[1])
          g.pinchOrigin    = { ...stagePos }
        } else if (touches.length === 1) {
          // 1포인터 — 팬/탭 판별 시작
          g.pinchActive   = false
          g.isPointerDown = true
          g.moved         = false
          g.pointerStart  = { x: touches[0].clientX, y: touches[0].clientY }
          g.panOrigin     = { ...stagePos }
        }
      } : undefined}
      onTouchMove={isMobile ? (e: any) => {
        const touches: TouchList = e.evt.touches
        const g = gestureRef.current
        if (g.pinchActive && touches.length === 2) {
          e.evt.preventDefault?.()
          const dist = touchDist(touches[0], touches[1])
          if (g.pinchStartDist > 0) {
            const ratio = dist / g.pinchStartDist
            applyZoom(g.pinchStartScale * ratio, g.pinchCenter)
          }
        } else if (g.isPointerDown && touches.length === 1) {
          const dx = touches[0].clientX - g.pointerStart.x
          const dy = touches[0].clientY - g.pointerStart.y
          if (!g.moved && Math.hypot(dx, dy) > TAP_MOVE_THRESHOLD) {
            g.moved = true  // 임계 초과 → 팬으로 확정, 이후 탭 무효
          }
          if (g.moved && zoomScale > 1) {
            e.evt.preventDefault?.()
            applyPan(g.panOrigin, dx, dy)
          }
        }
      } : undefined}
      onTouchEnd={isMobile ? (e: any) => {
        const g = gestureRef.current
        const remaining: TouchList = e.evt.touches
        // 핀치 종료 (포인터 2→1 이하)
        if (g.pinchActive && remaining.length < 2) {
          g.pinchActive = false
          // 남은 1포인터를 새 팬 시작점으로 — 핀치 후 바로 탭/팬 오인 방지 위해 isPointerDown 미설정
          g.isPointerDown = false
          return
        }
        // 1포인터 종료 — 탭 판정.
        //   SEQ=10081 fix: moved 플래그는 zoomScale>1(실제 패닝 가능) 일 때만 탭을 무효화한다.
        //   zoomScale===1 에서는 패닝이 일어나지 않으므로(applyPan early-return),
        //   손가락 미세 흔들림(>10px)으로 moved=true 가 돼도 탭/스냅을 막으면 안 됨.
        //   특히 가는 edge(도로) 탭은 손가락 시프트가 잦아 이게 도로 가이드 터치 실패의 원인.
        const tapBlockedByPan = g.moved && zoomScale > 1
        if (g.isPointerDown && !tapBlockedByPan) {
          g.isPointerDown = false
          g.moved = false
          const ct: Touch | undefined = e.evt.changedTouches?.[0]
          if (!ct) return
          const now = Date.now()
          const tapPos = { x: ct.clientX, y: ct.clientY }
          const sinceLast = now - g.lastTapTime
          const distLast = Math.hypot(tapPos.x - g.lastTapPos.x, tapPos.y - g.lastTapPos.y)
          if (sinceLast < DOUBLE_TAP_MS && distLast < 30) {
            // 더블탭 — 줌 토글 (줌인 상태면 전체 보기 / 아니면 탭 지점 줌인)
            if (zoomScale > 1.05) {
              resetView()
            } else {
              applyZoom(DOUBLE_TAP_SCALE, tapPos)
            }
            g.lastTapTime = 0  // 트리플탭 방지
          } else {
            g.lastTapTime = now
            g.lastTapPos  = tapPos
            // 대책 E: 단일 탭이 shape 에 직접 안 닿았으면 최근접 스냅 보조.
            //   shape onTap 이 이미 처리했으면 e.target 이 stage 가 아님 → 스냅 스킵.
            const stage = stageRef.current
            if (stage && e.target === stage) {
              const lp = stage.getRelativePointerPosition?.()
              if (lp) snapToNearest(lp.x, lp.y)
            }
          }
        } else {
          // 팬으로 끝난 경우(zoomScale>1 + moved) — 상태만 정리
          g.isPointerDown = false
          g.moved = false
        }
      } : undefined}
    >
      <Layer>
        {/* ── 헥스 19개 ───────────────────────────────────── */}
        {hexPixels.map(h => {
          const fillFallback = RESOURCE_COLOR[h.resourceType] ?? '#888'
          const isDesert = h.resourceType === 'DESERT'
          const isHot   = h.numberToken === 6 || h.numberToken === 8
          const dots    = h.numberToken != null ? dotCount(h.numberToken) : 0

          const tileImg = hexImg(h.resourceType)
          // hex 자원 아이콘(자원 5종 png) — 사막은 미표기
          const resImg = !isDesert ? resourceImg(h.resourceType) : undefined
          const resIconSize = hexSize * 0.42

          const isRobberMode = isMyTurn && currentPhase === 'MAIN_TURN_MOVE_ROBBER'
          /* 2026-05-07 fix: KNIGHT 카드 인라인 모드도 헥스 클릭 활성화 (PREROLL/POSTROLL) */
          const isKnightHexMode = isMyTurn && devCardMode === 'KNIGHT_PICK_HEX'
          const isClickable  = (isRobberMode || isKnightHexMode) && !h.hasRobber
          // hex pointy-top 6 corner 좌표 (clipFunc + 외곽 stroke 공유)
          const cornerPts: number[] = []
          for (let c = 0; c < 6; c++) {
            const angle = (Math.PI / 180) * (60 * c - 30)
            cornerPts.push((hexSize - 2) * Math.cos(angle))
            cornerPts.push((hexSize - 2) * Math.sin(angle))
          }
          // 이미지 fit: hex 외접원 직경 = 2 * hexSize, 정사각 1024 자산을 외접원에 맞춰 배치
          const tileImgSide = (hexSize - 2) * 2
          return (
            <Group key={h.hexId} x={h.px} y={h.py}
                   onClick={isClickable ? () => onHexClick?.(h.hexId) : undefined}
                   onTap={isClickable ? () => onHexClick?.(h.hexId) : undefined}
                   onTouchStart={isClickable ? (e) => { e.evt.preventDefault?.() } : undefined}>
              {/* 헥스 본체 — hex-tile 자산 + clipFunc 마스킹 */}
              {tileImg ? (
                <Group
                  clipFunc={(ctx: any) => {
                    ctx.beginPath()
                    ctx.moveTo(cornerPts[0], cornerPts[1])
                    for (let i = 2; i < cornerPts.length; i += 2) {
                      ctx.lineTo(cornerPts[i], cornerPts[i + 1])
                    }
                    ctx.closePath()
                  }}
                >
                  <KonvaImage
                    x={-tileImgSide / 2}
                    y={-tileImgSide / 2}
                    width={tileImgSide}
                    height={tileImgSide}
                    image={tileImg}
                    listening={false}
                  />
                </Group>
              ) : (
                // 자산 로드 전 fallback (기존 색상 fill)
                <RegularPolygon
                  sides={6}
                  radius={hexSize - 2}
                  rotation={0}
                  fill={fillFallback}
                  listening={false}
                />
              )}
              {/* hex 외곽 stroke — 클릭 가능 시 주황 강조, 그 외 어두운 회색 */}
              <RegularPolygon
                sides={6}
                radius={hexSize - 2}
                rotation={0}
                fill="transparent"
                stroke={isClickable ? '#FB8C00' : '#444'}
                strokeWidth={isClickable ? 4 : 2}
                listening={false}
              />

              {/* 자원 아이콘 (헥스 상단, png 5종 — 사막 제외) */}
              {resImg && (
                <KonvaImage
                  x={-(resIconSize / 2)}
                  y={-(hexSize * 0.66)}
                  width={resIconSize}
                  height={resIconSize}
                  image={resImg}
                  listening={false}
                />
              )}

              {/* 숫자 토큰 (사막 제외) */}
              {!isDesert && h.numberToken != null && (
                <>
                  <Circle radius={hexSize * 0.32} fill="#fffceb" stroke="#ccc" strokeWidth={1} />
                  <Text
                    text={String(h.numberToken)}
                    fontSize={hexSize * 0.36}
                    fontStyle="bold"
                    fill={isHot ? '#cc2200' : '#222'}
                    width={hexSize * 0.64}
                    height={hexSize * 0.36}
                    x={-(hexSize * 0.32)}
                    y={-(hexSize * 0.22)}
                    align="center"
                  />
                  {/* 확률 점 */}
                  {Array.from({ length: dots }).map((_, di) => (
                    <Circle
                      key={di}
                      x={(di - (dots - 1) / 2) * (hexSize * 0.09)}
                      y={hexSize * 0.18}
                      radius={hexSize * 0.04}
                      fill={isHot ? '#cc2200' : '#444'}
                    />
                  ))}
                </>
              )}

              {/* 도적 표시 (이미지 자산) */}
              {h.hasRobber && (
                robberImg ? (
                  <KonvaImage
                    x={-(hexSize * 0.28)}
                    y={-(hexSize * 0.32)}
                    width={hexSize * 0.56}
                    height={hexSize * 0.56}
                    image={robberImg}
                    listening={false}
                  />
                ) : (
                  // 자산 로드 전 fallback
                  <>
                    <Circle radius={hexSize * 0.2} fill="#222" opacity={0.8} />
                    <Text
                      text="R"
                      fontSize={hexSize * 0.24}
                      fontStyle="bold"
                      fill="#fff"
                      width={hexSize * 0.4}
                      height={hexSize * 0.3}
                      x={-(hexSize * 0.2)}
                      y={-(hexSize * 0.15)}
                      align="center"
                    />
                  </>
                )
              )}
            </Group>
          )
        })}

        {/* ── 항구 9개 (실 vertex 페어 기반 + dock rope + 권한 마커) ── */}
        {portItems.map(item => {
          const p = item.port
          const isActive = activePortId === p.portId
          const hasPrivilege = myPortPrivilege.has(p.portId)
          const ropeColor = p.ratio === 3
            ? '#8ba8c8'
            : (RESOURCE_COLOR[p.resourceType ?? ''] ?? '#8ba8c8')
          const img = portImg(p)
          const v1Occ = vertices.find(v => v.vertexId === p.vertexId1)?.ownerUserCode != null
          const v2Occ = vertices.find(v => v.vertexId === p.vertexId2)?.ownerUserCode != null
          const ratioBadge = p.ratio === 3 ? '3:1' : `2:1`

          return (
            <Group key={`port-${p.portId}`}>
              {/* dock rope 2개: vertex_id_1 ↔ port center, vertex_id_2 ↔ port center */}
              <Line
                points={[item.v1.x, item.v1.y, item.px, item.py]}
                stroke={ropeColor}
                strokeWidth={Math.max(2, hexSize * 0.07)}
                opacity={isActive ? 0.95 : 0.55}
                dash={[hexSize * 0.18, hexSize * 0.12]}
                lineCap="round"
                listening={false}
              />
              <Line
                points={[item.v2.x, item.v2.y, item.px, item.py]}
                stroke={ropeColor}
                strokeWidth={Math.max(2, hexSize * 0.07)}
                opacity={isActive ? 0.95 : 0.55}
                dash={[hexSize * 0.18, hexSize * 0.12]}
                lineCap="round"
                listening={false}
              />

              {/* 항구 자산 (Konva.Image) + 본인 권한 글로우 */}
              <Group x={item.px} y={item.py}>
                {/* 본인 권한 글로우: 자산 뒤 금색 원 */}
                {hasPrivilege && (
                  <Circle
                    radius={portAssetSize * 0.55}
                    fill="rgba(255, 215, 0, 0.25)"
                    stroke="#ffd54a"
                    strokeWidth={3}
                    shadowColor="#ffaa00"
                    shadowBlur={hexSize * 0.4}
                    shadowOpacity={0.85}
                    listening={false}
                  />
                )}
                {/* 활성(hover/탭) 강조: 외곽 금색 stroke */}
                {isActive && (
                  <Circle
                    radius={portAssetSize * 0.5}
                    stroke="#ffcc00"
                    strokeWidth={4}
                    opacity={0.85}
                    listening={false}
                  />
                )}
                {/* 자산 본체 */}
                {img ? (
                  <KonvaImage
                    x={-portAssetSize / 2}
                    y={-portAssetSize / 2}
                    width={portAssetSize}
                    height={portAssetSize}
                    image={img}
                  />
                ) : (
                  // 로드 전 fallback (기존 디자인 유지)
                  <Circle
                    radius={hexSize * 0.36}
                    fill={p.ratio === 3 ? '#8ba8c8' : (RESOURCE_COLOR[p.resourceType ?? ''] ?? '#0a8ff5')}
                    opacity={0.85}
                    stroke="#222"
                    strokeWidth={1.5}
                  />
                )}

                {/* 비율 배지 (자산 위 중앙 하단) */}
                <Group y={portAssetSize * 0.32}>
                  <Rect
                    x={-hexSize * 0.34}
                    y={-hexSize * 0.13}
                    width={hexSize * 0.68}
                    height={hexSize * 0.32}
                    fill="#ffffff"
                    stroke="#222"
                    strokeWidth={1}
                    cornerRadius={hexSize * 0.06}
                    opacity={0.92}
                    listening={false}
                  />
                  <Text
                    text={p.ratio === 3 ? '3:1' : `2:1 ${portResourceShort(p.resourceType)}`}
                    fontSize={hexSize * 0.18}
                    fontStyle="bold"
                    fill="#222"
                    width={hexSize * 0.68}
                    height={hexSize * 0.32}
                    x={-hexSize * 0.34}
                    y={-hexSize * 0.07}
                    align="center"
                    listening={false}
                  />
                </Group>

                {/* 3:1·2:1 텍스트 overlay — 디자인 가이드 §3-3-1 (2026-05-01)
                    SDXL이 항구 sign 자산에 정확한 비율 텍스트를 그리지 못하는 한계 보강.
                    자산 중앙(top half)에 Konva.Text로 직접 합성. wooden plaque 톤 일치. */}
                <Text
                  x={-portAssetSize / 2}
                  y={-portAssetSize * 0.32 - (portAssetSize * 0.28) * 0.6}
                  width={portAssetSize}
                  height={(portAssetSize * 0.28) * 1.2}
                  text={p.ratio === 3 ? '3:1' : '2:1'}
                  fontSize={portAssetSize * 0.28}
                  fontStyle="bold"
                  fontFamily="Georgia, serif"
                  fill="#3a2418"
                  align="center"
                  verticalAlign="middle"
                  shadowColor="#fff"
                  shadowBlur={3}
                  shadowOpacity={0.6}
                  listening={false}
                />

                {/* 클릭/탭 hit area (자산 전체) — Phase 5 onPortClick 진입 추가 (D-8) */}
                <Circle
                  radius={portAssetSize * 0.5}
                  fill="rgba(0,0,0,0.001)"
                  onMouseEnter={() => setActivePortId(p.portId)}
                  onMouseLeave={() => setActivePortId(null)}
                  onClick={() => {
                    setActivePortId(prev => prev === p.portId ? null : p.portId)
                    if (hasPrivilege && onPortClick) onPortClick(p.portId)
                  }}
                  onTap={() => {
                    setActivePortId(prev => prev === p.portId ? null : p.portId)
                    if (hasPrivilege && onPortClick) onPortClick(p.portId)
                  }}
                  onTouchStart={(e) => { e.evt.preventDefault?.() }}
                />
              </Group>

              {/* vertex 권한 마커 (미점유 vertex만) — 사용자가 "어디까지 항구"인지 즉시 식별 */}
              {!v1Occ && (
                <Group x={item.v1.x} y={item.v1.y} listening={false}>
                  <Circle
                    radius={hexSize * 0.16}
                    fill={ropeColor}
                    stroke="#222"
                    strokeWidth={1.5}
                    opacity={0.92}
                  />
                  <Text
                    text={ratioBadge}
                    fontSize={hexSize * 0.14}
                    fontStyle="bold"
                    fill="#fff"
                    width={hexSize * 0.5}
                    height={hexSize * 0.2}
                    x={-(hexSize * 0.25)}
                    y={-(hexSize * 0.09)}
                    align="center"
                  />
                </Group>
              )}
              {!v2Occ && (
                <Group x={item.v2.x} y={item.v2.y} listening={false}>
                  <Circle
                    radius={hexSize * 0.16}
                    fill={ropeColor}
                    stroke="#222"
                    strokeWidth={1.5}
                    opacity={0.92}
                  />
                  <Text
                    text={ratioBadge}
                    fontSize={hexSize * 0.14}
                    fontStyle="bold"
                    fill="#fff"
                    width={hexSize * 0.5}
                    height={hexSize * 0.2}
                    x={-(hexSize * 0.25)}
                    y={-(hexSize * 0.09)}
                    align="center"
                  />
                </Group>
              )}

              {/* 항구 활성(hover/탭) — 두 vertex에 금색 강조 stroke */}
              {isActive && (
                <>
                  <Circle
                    x={item.v1.x} y={item.v1.y}
                    radius={hexSize * 0.22}
                    stroke="#ffcc00"
                    strokeWidth={3}
                    opacity={0.85}
                    listening={false}
                  />
                  <Circle
                    x={item.v2.x} y={item.v2.y}
                    radius={hexSize * 0.22}
                    stroke="#ffcc00"
                    strokeWidth={3}
                    opacity={0.85}
                    listening={false}
                  />
                </>
              )}

              {/* 활성 시 한글 툴팁 (자산 위쪽) */}
              {isActive && (
                <Group x={item.px} y={item.py - portAssetSize * 0.55} listening={false}>
                  <Rect
                    x={-hexSize * 1.6}
                    y={-hexSize * 0.22}
                    width={hexSize * 3.2}
                    height={hexSize * 0.44}
                    fill="#222"
                    cornerRadius={hexSize * 0.08}
                    opacity={0.92}
                  />
                  <Text
                    text={portTooltip(p)}
                    fontSize={hexSize * 0.22}
                    fill="#fff"
                    width={hexSize * 3.2}
                    height={hexSize * 0.44}
                    x={-hexSize * 1.6}
                    y={-hexSize * 0.13}
                    align="center"
                  />
                </Group>
              )}
            </Group>
          )
        })}

        {/* ── edge 점유 도로 렌더링 (Line 유지: 플레이어 색 동적) ── */}
        {edges.map(e => {
          if (!e.ownerUserCode || !e.buildingType) return null
          const px = edgePixels[e.edgeId]
          if (!px) return null
          const color = playerColorMap[e.ownerUserCode] ?? '#ccc'
          return (
            <Line
              key={`road-${e.edgeId}`}
              points={[px.x1, px.y1, px.x2, px.y2]}
              stroke={color}
              strokeWidth={Math.max(5, hexSize * 0.13)}
              lineCap="round"
              listening={false}
            />
          )
        })}

        {/* ── vertex 점유 마을/도시 렌더링 (Konva.Image + 플레이어 색상 배경)
              2026-05-07 변경: 우상단 색상 원 마커 제거 → 이미지 뒤에 플레이어 색상 Rect 배경.
              배경 Rect가 이미지보다 약 18% 크게 → 사방에 색상 테두리 노출되어 소유주 명확 표시.
              도시 'C' 배지는 마을/도시 시각 구분 보조로 유지. */}
        {vertices.map(v => {
          if (!v.ownerUserCode || !v.buildingType) return null
          const px = vertexPixels[v.vertexId]
          if (!px) return null
          const color = playerColorMap[v.ownerUserCode] ?? '#ccc'
          const isCity = v.buildingType === 'CITY'
          const img = isCity ? cityImg : settlementImg
          const size = hexSize * (isCity ? 0.66 : 0.56)
          // 배경 Rect 크기 = 이미지 크기 × 1.18 (사방에 9% 색상 패딩 노출)
          const bgSize = size * 1.18
          const bgRadius = bgSize * 0.12  // 둥근 모서리
          const markerR = hexSize * (isCity ? 0.14 : 0.12)
          return (
            <Group key={`building-${v.vertexId}`} x={px.x} y={px.y} listening={false}>
              {/* 플레이어 색상 배경 Rect (이미지 뒤) */}
              <Rect
                x={-bgSize / 2}
                y={-bgSize / 2}
                width={bgSize}
                height={bgSize}
                fill={color}
                stroke="#222"
                strokeWidth={1.5}
                cornerRadius={bgRadius}
              />
              {img ? (
                <KonvaImage
                  x={-size / 2}
                  y={-size / 2}
                  width={size}
                  height={size}
                  image={img}
                />
              ) : (
                // 로드 전 fallback (기존 RegularPolygon 유지)
                <RegularPolygon
                  sides={isCity ? 4 : 5}
                  radius={hexSize * (isCity ? 0.18 : 0.16)}
                  fill={color}
                  stroke="#222"
                  strokeWidth={1.5}
                  rotation={isCity ? 45 : 0}
                />
              )}
              {/* 도시 식별 배지 (자산 우하단 'C') — 마을/도시 시각 구분 보조 */}
              {isCity && (
                <Group x={size * 0.32} y={size * 0.32}>
                  <Circle radius={markerR * 0.95} fill="#222" />
                  <Text
                    text="C"
                    fontSize={markerR * 1.2}
                    fontStyle="bold"
                    fill="#fff"
                    width={markerR * 2}
                    height={markerR * 1.4}
                    x={-markerR}
                    y={-markerR * 0.7}
                    align="center"
                  />
                </Group>
              )}
            </Group>
          )
        })}

        {/* SEQ=10114 P8-CK-C — 도시 성벽 마커 (도시 vertex 위) */}
        {(wallVertexIds ?? []).map(vid => {
          const px = vertexPixels[vid]
          if (!px) return null
          const r = hexSize * 0.18
          return (
            <Group key={`wall-${vid}`} x={px.x + r * 1.6} y={px.y - r * 1.6} listening={false}>
              <Text
                x={-r}
                y={-r}
                width={r * 2}
                fontSize={r * 1.8}
                align="center"
                text="🧱"
              />
            </Group>
          )
        })}

        {/* SEQ=10113 P8-CK-B — 기사 토큰 (vertex 위, LEVEL별 색 + Active/Inactive opacity 차이) */}
        {/* SEQ=10125 P8-CK-H2 (a) — knightClickMode 활성 시 listening + 진보카드 클릭 dispatch
            DESERTER: 모든 상대 기사 클릭 가능 (active/inactive 무관)
            INTRIGUE: 상대 활성 기사만 클릭 가능 */}
        {(knights ?? []).map(k => {
          const px = vertexPixels[k.vertexId]
          if (!px) return null
          const color = playerColorMap[k.userCode] ?? '#888'
          const r = hexSize * 0.32
          // LEVEL별 stroke 두께 + 원 안 점수 표기
          const strokeW = k.level === 3 ? 4 : (k.level === 2 ? 3 : 2)
          // 진보카드 모드별 클릭 가능 판정
          const isOpponent     = k.userCode !== myUserCode
          const isClickableDes = knightClickMode === 'DESERTER' && isOpponent
          const isClickableInt = knightClickMode === 'INTRIGUE' && isOpponent && k.isActive
          const isClickable    = isClickableDes || isClickableInt
          return (
            <Group
              key={`knight-${k.knightId}`}
              x={px.x}
              y={px.y}
              listening={isClickable}
              onTap={isClickable ? () => onKnightClick && onKnightClick(k.knightId, k.userCode) : undefined}
              onClick={isClickable ? () => onKnightClick && onKnightClick(k.knightId, k.userCode) : undefined}
            >
              {/* 클릭 활성 시 외곽 강조 ring (DESERTER=적색, INTRIGUE=보라) */}
              {isClickable && (
                <Circle
                  radius={r + 4}
                  stroke={knightClickMode === 'DESERTER' ? '#ff5252' : '#b266ff'}
                  strokeWidth={3}
                  dash={[4, 3]}
                  opacity={0.85}
                  listening={false}
                />
              )}
              <Circle
                radius={r}
                fill={color}
                stroke="#fff"
                strokeWidth={strokeW}
                opacity={k.isActive ? 1 : 0.55}
              />
              <Text
                x={-r}
                y={-r * 0.5}
                width={r * 2}
                fontSize={r * 0.9}
                fill="#fff"
                align="center"
                text={k.level === 3 ? 'M' : (k.level === 2 ? 'S' : 'K')}
                opacity={k.isActive ? 1 : 0.6}
                listening={false}
              />
            </Group>
          )
        })}

        {/* ── edge 72개 클릭 영역 + 도로 배치 가이드 ──────── */}
        {Array.from({ length: 72 }, (_, i) => i + 1).map(eId => {
          const px = edgePixels[eId]
          if (!px) return null
          const clickable = isEdgeClickable(eId)
          const isHovered = hoverEdgeId === eId && clickable

          // PLACE_ROAD 페이즈 + BUILD_ROAD 모드 + ROAD_BUILDING 카드 인라인 모드 모두 edge 가이드 표기 (점선)
          // 2026-05-07 fix: ROAD_BUILDING 카드 모드도 BUILD_ROAD와 동일 가이드, 색상만 보라로 차별화
          const isRoadBuildingCardMode = devCardMode === 'ROAD_BUILDING_PICK_EDGE'
          const showEdgeGuide = isMyTurn && clickable && (
            currentPhase === 'PLACE_ROAD' ||
            (currentPhase === 'MAIN_TURN_POSTROLL' && buildMode === 'BUILD_ROAD') ||
            isRoadBuildingCardMode
          )
          const buildRoadActive = buildMode === 'BUILD_ROAD' && currentPhase === 'MAIN_TURN_POSTROLL'
          // 가이드 색상 분기: ROAD_BUILDING 카드(보라 #b266ff) > BUILD_ROAD(본인색) > SETUP(주황 #ffaa30)
          const edgeGuideStroke = isRoadBuildingCardMode
            ? '#b266ff'
            : buildRoadActive
              ? (playerColorMap[myUserCode] ?? '#3a8dff')
              : '#ffaa30'

          return (
            <React.Fragment key={`edge-group-${eId}`}>
              {/* 배치 가능 가이드 — 주황(SETUP) / 본인색(BUILD_ROAD) / 보라(ROAD_BUILDING 카드) 점선 */}
              {showEdgeGuide && !isHovered && (
                <Line
                  points={[px.x1, px.y1, px.x2, px.y2]}
                  stroke={edgeGuideStroke}
                  strokeWidth={4}
                  lineCap="round"
                  dash={[6, 5]}
                  opacity={0.75}
                  listening={false}
                />
              )}
              {/* 호버 강조 — BUILD_ROAD 파랑 / ROAD_BUILDING 카드 옅은 보라 / SETUP 노랑 */}
              {showEdgeGuide && isHovered && (
                <Line
                  points={[px.x1, px.y1, px.x2, px.y2]}
                  stroke={isRoadBuildingCardMode ? '#d9a3ff' : (buildRoadActive ? '#7fc3ff' : '#ffd060')}
                  strokeWidth={6}
                  lineCap="round"
                  opacity={0.95}
                  listening={false}
                />
              )}
              {/* 클릭 히트박스 (시각 stroke와 hit stroke 분리: 모바일 터치 면적 ~32px) */}
              {/* SEQ=10081 fix: listening 을 clickable 기준으로 축소 (vertex 와 동일 취지).
                  비대상 edge 가 탭을 흡수하지 않도록 — 특히 PLACE_SETTLEMENT 에서 빈 edge 가
                  vertex 탭을 가로채지 않게. clickable 한 edge 만 listening. */}
              <Line
                points={[px.x1, px.y1, px.x2, px.y2]}
                stroke={(!showEdgeGuide && isHovered) ? 'rgba(255,255,255,0.7)' : 'transparent'}
                strokeWidth={edgeHitWidth}
                hitStrokeWidth={edgeHitStrokeWidth}
                lineCap="round"
                listening={clickable}
                onMouseEnter={() => { if (clickable) setHoverEdgeId(eId) }}
                onMouseLeave={() => setHoverEdgeId(null)}
                onClick={() => { if (clickable) onEdgeClick(eId) }}
                onTap={() => { if (clickable) onEdgeClick(eId) }}
                onTouchStart={(e) => {
                  if (clickable) {
                    setHoverEdgeId(eId)
                    e.evt.preventDefault?.()  // 페이지 스크롤·핀치줌과 탭 충돌 방지
                  }
                }}
                onTouchEnd={() => setHoverEdgeId(null)}
              />
            </React.Fragment>
          )
        })}

        {/* ── vertex 54개 클릭 영역 + 배치 가이드 하이라이트 ── */}
        {Array.from({ length: 54 }, (_, i) => i + 1).map(vId => {
          const px = vertexPixels[vId]
          if (!px) return null
          const clickable = isVertexClickable(vId)
          const isHovered = hoverVertexId === vId && clickable

          // ── 가이드 표기: PLACE_SETTLEMENT(초록) / BUILD_SETTLEMENT(파랑) / BUILD_CITY(금색) ──
          const isBuildSettlement = currentPhase === 'MAIN_TURN_POSTROLL' && buildMode === 'BUILD_SETTLEMENT'
          const isBuildCity       = currentPhase === 'MAIN_TURN_POSTROLL' && buildMode === 'BUILD_CITY'
          const showGuide = isMyTurn && clickable && (
            currentPhase === 'PLACE_SETTLEMENT' || isBuildSettlement || isBuildCity
          )
          const guideFill   = isBuildCity ? 'rgba(255,200,40,0.28)'
                            : isBuildSettlement ? 'rgba(60,140,255,0.26)'
                            : 'rgba(60,220,80,0.22)'
          const guideStroke = isBuildCity ? '#ffcc33'
                            : isBuildSettlement ? '#3a8dff'
                            : '#3cdc50'
          const guideHoverFill   = isBuildCity ? 'rgba(255,200,40,0.6)'
                            : isBuildSettlement ? 'rgba(60,140,255,0.55)'
                            : 'rgba(60,220,80,0.55)'
          const guideHoverStroke = isBuildCity ? '#ffe080'
                            : isBuildSettlement ? '#7fc3ff'
                            : '#7fff90'

          return (
            <React.Fragment key={`vertex-group-${vId}`}>
              {/* 배치 가능 가이드 — SETUP(초록) / BUILD_SETTLEMENT(파랑) / BUILD_CITY(금색) */}
              {showGuide && !isHovered && (
                <Circle
                  x={px.x}
                  y={px.y}
                  radius={vertexHitRadius * 0.85}
                  fill={guideFill}
                  stroke={guideStroke}
                  strokeWidth={1.5}
                  listening={false}
                />
              )}
              {/* 호버 하이라이트 */}
              {showGuide && isHovered && (
                <Circle
                  x={px.x}
                  y={px.y}
                  radius={vertexHitRadius * 1.05}
                  fill={guideHoverFill}
                  stroke={guideHoverStroke}
                  strokeWidth={2}
                  listening={false}
                />
              )}
              {/* 클릭 히트박스 (모바일 hitStrokeWidth로 터치 영역 확장) */}
              {/* SEQ=10081 fix: listening 을 clickable 기준으로 축소.
                  기존 `!occupiedVertexIds.has(vId) || isBuildCity` 는 도로 페이즈(PLACE_ROAD/
                  BUILD_ROAD/ROAD_BUILDING)에서 클릭 불가한 모든 빈 vertex 도 listening → 큰
                  vertex 히트박스(~60px)가 edge 끝점 근처 탭을 흡수해 도로 가이드 터치 실패.
                  clickable 한 vertex 만 listening 하면 비대상 vertex 가 탭을 흘려보내
                  edge 히트박스/Stage 스냅으로 전달됨. clickable=isVertexClickable(vId) 는
                  BUILD_CITY 후보(본인 마을)도 포함하므로 도시 업그레이드도 정상. */}
              <Circle
                x={px.x}
                y={px.y}
                radius={vertexHitRadius}
                fill={(!showGuide && isHovered) ? 'rgba(255,255,255,0.35)' : 'transparent'}
                stroke={(!showGuide && isHovered) ? '#fff' : undefined}
                strokeWidth={(!showGuide && isHovered) ? 2 : 0}
                hitStrokeWidth={vertexHitStrokeWidth}
                listening={clickable}
                onMouseEnter={() => { if (clickable) setHoverVertexId(vId) }}
                onMouseLeave={() => setHoverVertexId(null)}
                onClick={() => { if (clickable) onVertexClick(vId) }}
                onTap={() => { if (clickable) onVertexClick(vId) }}
                onTouchStart={(e) => {
                  if (clickable) {
                    setHoverVertexId(vId)
                    e.evt.preventDefault?.()
                  }
                }}
                onTouchEnd={() => setHoverVertexId(null)}
              />
            </React.Fragment>
          )
        })}
      </Layer>
    </Stage>
    {/* ── SEQ=10080 모바일 "전체 보기" 리셋 버튼 — 줌인 상태에서만 노출 ──
         더블탭으로도 리셋 가능(onTouchEnd 핸들러)하지만 명시적 버튼도 함께 제공(D-5). */}
    {isMobile && zoomScale > 1.05 && (
      <button
        type="button"
        onClick={resetView}
        onTouchEnd={(e) => { e.preventDefault(); resetView() }}
        style={{
          position: 'absolute',
          right: 8,
          bottom: 8,
          zIndex: 5,
          padding: '6px 12px',
          fontSize: 12,
          fontWeight: 700,
          color: '#fff',
          background: 'rgba(20,30,55,0.88)',
          border: '1px solid #4a9eff',
          borderRadius: 6,
          lineHeight: 1.2,
          WebkitTapHighlightColor: 'transparent',
          touchAction: 'none',
        }}
      >
        🔍 전체 보기
      </button>
    )}
    </div>
  )
}

// 외부에서 hexCornerPixel 활용할 수 있도록 export (Phase 3 vertex 좌표 계산 시)
export { axialToPixel, hexCornerPixel }
